(function () {
  "use strict";

  const BERLIN_TIMEZONE = "Europe/Berlin";

  function toInt(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function parseList(value, fallback) {
    const source = String(value || fallback || "").toUpperCase().replace(/\s+/g, "");
    return source ? source.split(",").filter(Boolean) : [];
  }

  function getConfig(root) {
    return {
      cutoffHour: toInt(root.dataset.cutoffHour, 14),
      de: { min: toInt(root.dataset.deMinDays, 1), max: toInt(root.dataset.deMaxDays, 2) },
      at: { min: toInt(root.dataset.atMinDays, 2), max: toInt(root.dataset.atMaxDays, 3) },
      eu: { min: toInt(root.dataset.euMinDays, 2), max: toInt(root.dataset.euMaxDays, 4) },
      intl: { min: toInt(root.dataset.intlMinDays, 3), max: toInt(root.dataset.intlMaxDays, 7) },
      euCountries: parseList(root.dataset.euCountries, "BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE")
    };
  }

  function getBerlinNowParts() {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: BERLIN_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    });

    const parts = formatter.formatToParts(new Date());
    const map = {};
    parts.forEach((part) => {
      if (part.type !== "literal") map[part.type] = part.value;
    });

    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      hour: parseInt(map.hour, 10),
      minute: parseInt(map.minute, 10),
      second: parseInt(map.second, 10)
    };
  }

  function makeUtcDateFromBerlinParts(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  }

  function addUtcDays(date, days) {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  }

  function getUtcWeekday(date) {
    return date.getUTCDay();
  }

  function isShippingDay(date) {
    const day = getUtcWeekday(date);
    return day >= 1 && day <= 5;
  }

  function isDeliveryDay(date) {
    const day = getUtcWeekday(date);
    return day !== 0;
  }

  function getShipDate(cutoffHour) {
    const nowBerlin = getBerlinNowParts();
    let shipDate = makeUtcDateFromBerlinParts(nowBerlin);

    const weekday = getUtcWeekday(shipDate);
    const beforeCutoff = nowBerlin.hour < cutoffHour;

    if (weekday >= 1 && weekday <= 5 && beforeCutoff) {
      return shipDate;
    }

    do {
      shipDate = addUtcDays(shipDate, 1);
    } while (!isShippingDay(shipDate));

    return shipDate;
  }

  function addDeliveryWorkingDays(startDate, count) {
    let date = new Date(startDate.getTime());
    let added = 0;

    while (added < count) {
      date = addUtcDays(date, 1);
      if (isDeliveryDay(date)) added += 1;
    }

    return date;
  }

  function getTransitRange(countryCode, cfg) {
    const code = String(countryCode || "DE").toUpperCase();

    if (code === "DE") return cfg.de;
    if (code === "AT") return cfg.at;
    if (cfg.euCountries.indexOf(code) !== -1) return cfg.eu;
    return cfg.intl;
  }

  function normalizeLocale(locale) {
    const value = String(locale || document.documentElement.lang || "de").toLowerCase();
    const map = { de: "de-DE", en: "en-GB", es: "es-ES", fr: "fr-FR", it: "it-IT", nl: "nl-NL" };
    if (map[value]) return map[value];
    const short = value.split("-")[0];
    return map[short] || "de-DE";
  }

  function formatLocalizedDate(date, locale) {
    return new Intl.DateTimeFormat(locale, {
      timeZone: "UTC",
      weekday: "short",
      day: "2-digit",
      month: "short"
    }).format(date);
  }

  function getEstimateMarkup(root) {
    const cfg = getConfig(root);
    const locale = normalizeLocale(root.dataset.locale);
    const shipDate = getShipDate(cfg.cutoffHour);
    const range = getTransitRange(root.dataset.countryCode, cfg);

    const minDays = Math.max(1, Math.min(range.min, range.max));
    const maxDays = Math.max(minDays, range.max);

    const earliest = addDeliveryWorkingDays(shipDate, minDays);
    const latest = addDeliveryWorkingDays(shipDate, maxDays);

    const availableText = root.dataset.textAvailable || "Sofort lieferbar";
    const deliveryText = root.dataset.textDelivery || "Voraussichtliche Lieferung:";
    const rangeSeparator = root.dataset.textRangeSeparator || "–";

    const earliestText = formatLocalizedDate(earliest, locale);
    const latestText = formatLocalizedDate(latest, locale);

    const deliveryMarkup =
      earliestText === latestText
        ? `<strong>${earliestText}</strong>`
        : `<strong>${earliestText} ${rangeSeparator} ${latestText}</strong>`;

    return `${availableText} – ${deliveryText} ${deliveryMarkup}`;
  }

  function updateEstimate(root, variant) {
    if (!root) return;
    const textNode = root.querySelector("[data-shipping-estimate-text]");
    if (!textNode) return;

    let available = root.dataset.available === "true";
    if (variant && typeof variant.available !== "undefined") available = !!variant.available;

    if (!available) {
      root.hidden = true;
      root.classList.add("is-unavailable");
      root.setAttribute("aria-hidden", "true");
      textNode.textContent = "";
      return;
    }

    root.hidden = false;
    root.classList.remove("is-unavailable");
    root.removeAttribute("aria-hidden");
    textNode.innerHTML = getEstimateMarkup(root);
  }

  function initEstimate(root) {
    if (!root || root.dataset.shippingEstimateInitialized === "true") return;
    root.dataset.shippingEstimateInitialized = "true";
    updateEstimate(root, null);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-shipping-estimate]").forEach(initEstimate);
  }

  document.addEventListener("DOMContentLoaded", function () {
    initAll(document);
  });

  document.addEventListener("product:variant-change", function (evt) {
    const variant = evt.detail && evt.detail.variant;
    document.querySelectorAll("[data-shipping-estimate]").forEach(function (root) {
      updateEstimate(root, variant);
    });
  });

  document.addEventListener("quickview:loaded", function () {
    initAll(document);
  });

  document.addEventListener("shopify:section:load", function (evt) {
    if (evt.target) initAll(evt.target);
  });
})();
